<template lang="pug">
el-config-provider(:locale="locale")
  .fill-height
    policy-modal(v-if="userInteractionRequired")
    app-header
    .app-content
      notifications-container
      router-view(:key="route.fullPath")
      app-footer
    //- .version.txt-smaller.txt-inactive v2.0
    .imprint.flex.flex-row
      a.txt-inactive.mr-1(
        v-if="getSetting('imprintLink') !== null",
        :href="getSetting('imprintLink')",
        target="_blank"
      ) {{ t("imprint") }}
      a.txt-inactive.mr-1(:href="privacyPolicyLink", target="_blank") {{ t("privacyPolicy") }}
      a.txt-inactive.mr-1(:href="termsAndConditionsLink", target="_blank") {{ t("termsAndConditions") }}
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import AppHeader from "@/components/header/AppHeader";
import AppFooter from "@/components/footer/AppFooter";
import NotificationsContainer from "@/components/elements/notifications/NotificationsContainer";
import PolicyModal from "@/components/auth/PolicyModal";

import de from "element-plus/dist/locale/de.mjs";
import en from "element-plus/dist/locale/en.mjs";
import fr from "element-plus/dist/locale/fr.mjs";
import bg from "element-plus/dist/locale/bg.mjs";
import cs from "element-plus/dist/locale/cs.mjs";
import el from "element-plus/dist/locale/el.mjs";
import hr from "element-plus/dist/locale/hr.mjs";
import hu from "element-plus/dist/locale/hu.mjs";
import it from "element-plus/dist/locale/it.mjs";
import lv from "element-plus/dist/locale/lv.mjs";
import pl from "element-plus/dist/locale/pl.mjs";
import pt from "element-plus/dist/locale/pt.mjs";
import ru from "element-plus/dist/locale/ru.mjs";
import ro from "element-plus/dist/locale/ro.mjs";
import es from "element-plus/dist/locale/es.mjs";
import tr from "element-plus/dist/locale/tr.mjs";
import sr from "element-plus/dist/locale/sr.mjs";
import et from "element-plus/dist/locale/et.mjs";
import nl from "element-plus/dist/locale/nl.mjs";
import ar from "element-plus/dist/locale/ar.mjs";

import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/bg";
import "dayjs/locale/cs";
import "dayjs/locale/el";
import "dayjs/locale/hr";
import "dayjs/locale/hu";
import "dayjs/locale/it";
import "dayjs/locale/lv";
import "dayjs/locale/pl";
import "dayjs/locale/pt";
import "dayjs/locale/ru";
import "dayjs/locale/ro";
import "dayjs/locale/es";
import "dayjs/locale/tr";
import "dayjs/locale/sr";
import "dayjs/locale/et";
import "dayjs/locale/nl";
import "dayjs/locale/ar";

import dayjs from "dayjs";
import { getSetting } from "./getSettings";
import { useRoute } from "vue-router";

const title = getSetting("title") ?? "eLearning Portal";

const store = useStore();
const { locale: i18nLocale, t } = useI18n();
const route = useRoute();

const userInteractionRequired = computed(
  () => store.getters.userInteractionRequired,
);
const getCurrentUserData = computed(() => store.getters.getCurrentUserData);

const privacyPolicyLink = computed(() => {
  return getSetting("privacyPolicyLink").replace(
    "$langcode",
    i18nLocale.value.toLowerCase(),
  );
});

const termsAndConditionsLink = computed(() => {
  return getSetting("termsAndConditionsLink").replace(
    "$langcode",
    i18nLocale.value.toLowerCase(),
  );
});

const locale = computed(() => {
  switch (
    (getCurrentUserData.value?.attributes?.langCode ?? "en").toLowerCase()
  ) {
    case "de":
      return de;
    case "en":
      return en;
    case "fr":
      return fr;
    case "bg":
      return bg;
    case "cs":
      return cs;
    case "el":
      return el;
    case "hr":
      return hr;
    case "hu":
      return hu;
    case "it":
      return it;
    case "lv":
      return lv;
    case "pl":
      return pl;
    case "pt":
      return pt;
    case "ru":
      return ru;
    case "ro":
      return ro;
    case "es":
      return es;
    case "tr":
      return tr;
    case "sr":
      return sr;
    case "et":
      return et;
    case "nl":
      return nl;
    case "ar":
      return ar;
  }
  return en;
});

const setDayjsLocale = () => {
  // Set dayjs locale based on the current language
  const langCode = (
    getCurrentUserData.value?.attributes?.langCode ?? "en"
  ).toLowerCase();
  if (langCode === "de") {
    dayjs.locale("de");
  } else if (langCode === "en") {
    dayjs.locale("en");
  } else if (langCode === "fr") {
    dayjs.locale("fr");
  } else if (langCode === "bg") {
    dayjs.locale("bg");
  } else if (langCode === "cs") {
    dayjs.locale("cs");
  } else if (langCode === "el") {
    dayjs.locale("el");
  } else if (langCode === "hr") {
    dayjs.locale("hr");
  } else if (langCode === "hu") {
    dayjs.locale("hu");
  } else if (langCode === "it") {
    dayjs.locale("it");
  } else if (langCode === "lv") {
    dayjs.locale("lv");
  } else if (langCode === "pl") {
    dayjs.locale("pl");
  } else if (langCode === "pt") {
    dayjs.locale("pt");
  } else if (langCode === "ru") {
    dayjs.locale("ru");
  } else if (langCode === "ro") {
    dayjs.locale("ro");
  } else if (langCode === "es") {
    dayjs.locale("es");
  } else if (langCode === "tr") {
    dayjs.locale("tr");
  } else if (langCode === "sr") {
    dayjs.locale("sr");
  } else if (langCode === "et") {
    dayjs.locale("et");
  } else if (langCode === "nl") {
    dayjs.locale("nl");
  } else if (langCode === "ar") {
    dayjs.locale("ar");
  }
};

// Initialize user data early so router guards can see the token
void store.dispatch("initUserData");

watch(locale, () => {
  setDayjsLocale();
});

onMounted(() => {
  if (title) document.title = title;
  setDayjsLocale();
});
</script>

<style lang="stylus">

@import './global.styl';

@import './settings.styl';

#app {
  height: 100%;
}

.app-content {
  height: 'calc(100% - %s)' % $headerHeight;
  margin: auto;
  padding: 0 1rem;
}

.imprint {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  font-size: 0.7rem;
}

.version {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: -1;
}

body {
  background-color: $background;
}
</style>
